import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/sign-in")({
  component: SignIn,
});

function SignIn() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [identifierError, setIdentifierError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showResendUI, setShowResendUI] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendEmailError, setResendEmailError] = useState<string | null>(null);
  const router = useRouter();

  // toast for email verification is handled at root to guarantee toaster mount

  // basic email detection used to choose sign-in method
  const isEmail = (value: string) =>
    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <div className="flex w-full flex-col items-center gap-6">
        <img
          src="/header-logo.png"
          alt="clove.moe"
          className="h-12 w-auto align-middle"
        />
        <Card className="z-50 w-full max-w-md rounded-md shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl text-[#353535]">sign in</CardTitle>
            <CardDescription className="text-xs md:text-sm text-accent">
              enter your email or username to log in
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="identifier" className="text-[#353535]">email or username</Label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder="me@example.com or username"
                  required
                  onChange={(e) => {
                    const v = e.target.value;
                    setIdentifier(v);
                    if (identifierError && v.trim().length > 0) {
                      setIdentifierError(null);
                    }
                    if (showResendUI) {
                      setShowResendUI(false);
                      setResendEmail("");
                      setResendEmailError(null);
                    }
                  }}
                  value={identifier}
                  aria-invalid={Boolean(identifierError) || undefined}
                  aria-describedby={identifierError ? "identifier-error" : undefined}
                  className={`${identifierError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"}`}
                />
                {identifierError && (
                  <p id="identifier-error" className="text-xs text-red-600 mt-1">{identifierError}</p>
                )}
                {identifierError && showResendUI && (
                  <div className="mt-1 space-y-2">
                    {isEmail(identifier.trim()) ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const email = identifier.trim();
                          setResendLoading(true);
                          await authClient.sendVerificationEmail(
                            { email, callbackURL: "/sign-in" },
                            {
                              onRequest: () => setResendLoading(true),
                              onResponse: () => setResendLoading(false),
                              onSuccess: () => {
                                toast.success("verification email sent");
                              },
                            }
                          );
                        }}
                        disabled={resendLoading}
                        className="text-xs text-accent underline disabled:opacity-50"
                      >
                        {resendLoading ? "sending..." : "resend verification email"}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          id="resend-email"
                          type="email"
                          placeholder="enter your email"
                          value={resendEmail}
                          onChange={(e) => {
                            const v = e.target.value;
                            setResendEmail(v);
                            if (resendEmailError && v.trim().length > 0) setResendEmailError(null);
                          }}
                          className={`h-8 ${resendEmailError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"}`}
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const email = resendEmail.trim();
                            if (!isEmail(email)) {
                              setResendEmailError("please enter a valid email address");
                              return;
                            }
                            setResendLoading(true);
                            await authClient.sendVerificationEmail(
                              { email, callbackURL: "/sign-in" },
                              {
                                onRequest: () => setResendLoading(true),
                                onResponse: () => setResendLoading(false),
                                onSuccess: () => {
                                  toast.success("verification email sent");
                                },
                              }
                            );
                          }}
                          disabled={resendLoading}
                          className="text-xs text-accent underline disabled:opacity-50"
                        >
                          {resendLoading ? "sending..." : "resend"}
                        </button>
                      </div>
                    )}
                    {resendEmailError && (
                      <p className="text-xs text-red-600">{resendEmailError}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password" className="text-[#353535]">password</Label>
                </div>

                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPassword(v);
                      if (passwordError && v.trim().length > 0) {
                        setPasswordError(null);
                      }
                    }}
                    aria-invalid={Boolean(passwordError) || undefined}
                    aria-describedby={passwordError ? "password-error" : undefined}
                    className={`${passwordError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {passwordError && (
                  <p id="password-error" className="text-xs text-red-600 mt-1">{passwordError}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  onClick={() => {
                    setRememberMe(!rememberMe);
                  }}
                  className="border-accent data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                />
                <Label htmlFor="remember" className="text-[#353535]">remember me</Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-accent text-white hover:opacity-90"
                disabled={loading}
                onClick={async () => {
                  let hasError = false;
                  const id = identifier.trim();
                  if (!id) {
                    setIdentifierError("please enter your email or username");
                    hasError = true;
                  }
                  if (!password.trim()) {
                    setPasswordError("please enter your password");
                    hasError = true;
                  }
                  if (hasError) return;

                  if (isEmail(id)) {
                    await authClient.signIn.email(
                      { email: id, password },
                      {
                        onRequest: () => setLoading(true),
                        onResponse: () => setLoading(false),
                        onError: (ctx) => {
                          const status = (ctx as any)?.error?.status;
                          if (status === 403) {
                            setIdentifierError("please verify your email then try again");
                            toast.error("please verify your email then try again");
                            setShowResendUI(true);
                            setResendEmail(id);
                          }
                          // other errors are handled by global auth client onError
                        },
                        onSuccess: async () => {
                          router.navigate({ to: "/dashboard" });
                        },
                      }
                    );
                  } else {
                    await authClient.signIn.username(
                      { username: id.toLowerCase(), password },
                      {
                        onRequest: () => setLoading(true),
                        onResponse: () => setLoading(false),
                        onError: (ctx) => {
                          const status = (ctx as any)?.error?.status;
                          if (status === 403) {
                            setIdentifierError("please verify your email then try again");
                            toast.error("please verify your email then try again");
                            setShowResendUI(true);
                            setResendEmail("");
                          }
                          // other errors are handled by global auth client onError
                        },
                        onSuccess: async () => {
                          router.navigate({ to: "/dashboard" });
                        },
                      }
                    );
                  }
                }}
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "login"
                )}
              </Button>
            </div>
          </CardContent>
          <CardFooter>
            <div className="flex justify-center w-full border-t pt-4">
              <div className="flex items-center gap-2">
                <a
                  href="/forgot-password"
                  className="ml-auto inline-block text-sm text-accent hover:text-accent underline cursor-pointer"
                >
                  forgot your password?
                </a>
                <a
                  href="/sign-up"
                  className="ml-auto inline-block text-sm text-accent hover:text-accent underline cursor-pointer"
                >
                  sign up
                </a>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
